import type { User, Organization, Test, Run, Integration, Pagination, RunStep, RunArtifact, IntegrationConfig, Persona, Workflow } from '../types/api'
import { SessionsApi, type BrowserSession, type CreateSessionRequest, type SessionListResponse } from './api/sessions'

// Use production API when accessing through production domain (app.smokestack.dev)
const isProductionDomain = window.location.hostname === 'app.smokestack.dev'
const API_BASE = isProductionDomain
  ? 'https://api.smokestack.dev/api'
  : import.meta.env?.DEV
    ? '/api'
    : 'https://api.smokestack.dev/api'

// Global variable to store the session token getter function
let getSessionToken: (() => Promise<string | null>) | null = null

// Function to set the session token getter (called from components with Clerk context)
export function setSessionTokenGetter(getter: () => Promise<string | null>) {
  getSessionToken = getter
}

class ApiClient {
  public sessions: SessionsApi

  constructor() {
    this.sessions = new SessionsApi(this)
  }

  // Public method for blob responses (like artifacts)
  async requestBlob(endpoint: string, options: RequestInit = {}): Promise<Blob> {
    const url = `${API_BASE}${endpoint}`

    // Get Clerk session token if available
    const headers: Record<string, string> = {
      ...(options.headers as Record<string, string> || {}),
    }

    if (getSessionToken) {
      try {
        const token = await getSessionToken()
        if (token) {
          headers['Authorization'] = `Bearer ${token}`
        }
      } catch (error) {
        // If Clerk token fails, continue without it (Cloudflare Access might handle auth)
        console.log('Clerk token not available, relying on Cloudflare Access authentication')
      }
    }

    const response = await fetch(url, {
      ...options,
      headers,
      credentials: 'include',
    })

    if (!response.ok) {
      const error = await response.text()
      throw new Error(`API Error (${response.status}): ${error}`)
    }

    return response.blob()
  }

  async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const url = `${API_BASE}${endpoint}`

    // Get Clerk session token if available
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string> || {}),
    }

    if (getSessionToken) {
      try {
        const token = await getSessionToken()
        if (token) {
          headers['Authorization'] = `Bearer ${token}`
        }
      } catch (error) {
        // If Clerk token fails, continue without it (Cloudflare Access might handle auth)
        console.log('Clerk token not available, relying on Cloudflare Access authentication')
      }
    }

    const response = await fetch(url, {
      ...options,
      headers,
      credentials: 'include',
    })

    if (!response.ok) {
      const error = await response.text()
      throw new Error(`API Error (${response.status}): ${error}`)
    }

    return response.json()
  }

  // User endpoints
  async getCurrentUser(): Promise<User> {
    return this.request<User>('/users/me')
  }

  // Organization endpoints
  async getCurrentOrganization(): Promise<Organization> {
    return this.request<Organization>('/organizations/current')
  }

  async getOrganizationMembers(): Promise<{ members: User[] }> {
    return this.request<{ members: User[] }>('/organizations/members')
  }

  async updateOrganizationProvider(config: { provider: string }): Promise<any> {
    return this.request('/organizations/provider', {
      method: 'PUT',
      body: JSON.stringify(config),
    })
  }

  async testOrganizationProvider(config: { provider: string }): Promise<any> {
    return this.request('/organizations/provider/test', {
      method: 'POST',
      body: JSON.stringify(config),
    })
  }

  // Test endpoints
  async getTests(params?: {
    page?: number
    limit?: number
    status?: string
  }): Promise<{ tests: Test[]; pagination: Pagination }> {
    const searchParams = new URLSearchParams()
    if (params?.page) searchParams.set('page', params.page.toString())
    if (params?.limit) searchParams.set('limit', params.limit.toString())
    if (params?.status) searchParams.set('status', params.status)

    const query = searchParams.toString()
    return this.request<{ tests: Test[]; pagination: Pagination }>(`/tests${query ? `?${query}` : ''}`)
  }

  async getTest(id: number): Promise<Test> {
    return this.request<Test>(`/tests/${id}`)
  }

  async createTest(test: {
    name: string
    base_url: string
    persona_id?: number
    schedule?: {
      cron: string
      timezone?: string
    }
  }): Promise<Test> {
    return this.request<Test>('/tests', {
      method: 'POST',
      body: JSON.stringify(test),
    })
  }

  async updateTest(id: number, updates: Partial<Test>): Promise<{ success: boolean }> {
    return this.request<{ success: boolean }>(`/tests/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    })
  }

  async deleteTest(id: number): Promise<{ success: boolean }> {
    return this.request<{ success: boolean }>(`/tests/${id}`, {
      method: 'DELETE',
    })
  }

  async updateTestSchedule(id: number, schedule: { cron: string; timezone: string }): Promise<{ success: boolean }> {
    return this.request<{ success: boolean }>(`/tests/${id}/schedule`, {
      method: 'PUT',
      body: JSON.stringify(schedule),
    })
  }

  async deleteTestSchedule(id: number): Promise<{ success: boolean }> {
    return this.request<{ success: boolean }>(`/tests/${id}/schedule`, {
      method: 'DELETE',
    })
  }

  async runTest(id: number): Promise<Run> {
    return this.request<Run>(`/tests/${id}/run`, {
      method: 'POST',
    })
  }

  // Persona endpoints
  async getPersonas(): Promise<{ personas: Persona[] }> {
    return this.request<{ personas: Persona[] }>('/personas')
  }

  async getPersona(id: number): Promise<Persona> {
    return this.request<Persona>(`/personas/${id}`)
  }

  async createPersona(persona: {
    name: string
    prompt: string
  }): Promise<Persona> {
    return this.request<Persona>('/personas', {
      method: 'POST',
      body: JSON.stringify(persona),
    })
  }

  async updatePersona(id: number, updates: {
    name?: string
    prompt?: string
  }): Promise<{ success: boolean }> {
    return this.request<{ success: boolean }>(`/personas/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    })
  }

  async deletePersona(id: number): Promise<{ success: boolean }> {
    return this.request<{ success: boolean }>(`/personas/${id}`, {
      method: 'DELETE',
    })
  }

  // Run endpoints
  async getRuns(params?: {
    page?: number
    limit?: number
    test_id?: number
    status?: string
  }): Promise<{ runs: Run[]; pagination: Pagination }> {
    const searchParams = new URLSearchParams()
    if (params?.page) searchParams.set('page', params.page.toString())
    if (params?.limit) searchParams.set('limit', params.limit.toString())
    if (params?.test_id) searchParams.set('test_id', params.test_id.toString())
    if (params?.status) searchParams.set('status', params.status)

    const query = searchParams.toString()
    return this.request<{ runs: Run[]; pagination: Pagination }>(`/runs${query ? `?${query}` : ''}`)
  }

  async getRun(id: number): Promise<Run & { steps: RunStep[]; artifacts: RunArtifact[] }> {
    return this.request<Run & { steps: RunStep[]; artifacts: RunArtifact[] }>(`/runs/${id}`)
  }

  async cancelRun(id: number): Promise<{ success: boolean }> {
    return this.request<{ success: boolean }>(`/runs/${id}/cancel`, {
      method: 'POST',
    })
  }

  async retryRun(id: number): Promise<Run> {
    return this.request<Run>(`/runs/${id}/retry`, {
      method: 'POST',
    })
  }

  // Workflow Run endpoints (New system)
  async getWorkflowRuns(params?: {
    page?: number
    limit?: number
    workflow_id?: number
    status?: string
  }): Promise<{ runs: any[]; pagination: Pagination }> {
    const searchParams = new URLSearchParams()
    if (params?.page) searchParams.set('page', params.page.toString())
    if (params?.limit) searchParams.set('limit', params.limit.toString())
    if (params?.workflow_id) searchParams.set('workflow_id', params.workflow_id.toString())
    if (params?.status) searchParams.set('status', params.status)

    const query = searchParams.toString()
    return this.request<{ runs: any[]; pagination: Pagination }>(`/workflow-runs${query ? `?${query}` : ''}`)
  }

  async getWorkflowRun(id: number): Promise<any> {
    return this.request<any>(`/workflow-runs/${id}`)
  }

  async cancelWorkflowRun(id: number): Promise<{ success: boolean }> {
    return this.request<{ success: boolean }>(`/workflow-runs/${id}/cancel`, {
      method: 'POST',
    })
  }

  async retryWorkflowRun(id: number): Promise<any> {
    return this.request<any>(`/workflow-runs/${id}/retry`, {
      method: 'POST',
    })
  }

  // Integration endpoints
  async getIntegrations(): Promise<{ integrations: Integration[] }> {
    return this.request<{ integrations: Integration[] }>('/integrations')
  }

  async createIntegration(integration: {
    type: 'slack' | 'email'
    config: IntegrationConfig
  }): Promise<Integration> {
    return this.request<Integration>('/integrations', {
      method: 'POST',
      body: JSON.stringify(integration),
    })
  }

  async updateIntegration(id: number, integration: {
    type: 'slack' | 'email'
    config: IntegrationConfig
  }): Promise<{ success: boolean }> {
    return this.request<{ success: boolean }>(`/integrations/${id}`, {
      method: 'PUT',
      body: JSON.stringify(integration),
    })
  }

  async toggleIntegration(id: number): Promise<{ enabled: boolean }> {
    return this.request<{ enabled: boolean }>(`/integrations/${id}/toggle`, {
      method: 'PATCH',
    })
  }

  async deleteIntegration(id: number): Promise<{ success: boolean }> {
    return this.request<{ success: boolean }>(`/integrations/${id}`, {
      method: 'DELETE',
    })
  }

  // Workflow methods
  async getWorkflows(params?: { page?: number; limit?: number; status?: string }): Promise<{ workflows: Workflow[]; pagination: Pagination }> {
    const searchParams = new URLSearchParams()
    if (params?.page) searchParams.set('page', params.page.toString())
    if (params?.limit) searchParams.set('limit', params.limit.toString())
    if (params?.status) searchParams.set('status', params.status)

    const query = searchParams.toString()
    return this.request<{ workflows: Workflow[]; pagination: Pagination }>(`/workflows${query ? `?${query}` : ''}`)
  }

  async getWorkflow(id: number): Promise<Workflow> {
    return this.request<Workflow>(`/workflows/${id}`)
  }

  async createWorkflow(workflow: Partial<Workflow>): Promise<Workflow> {
    return this.request<Workflow>('/workflows', {
      method: 'POST',
      body: JSON.stringify(workflow),
    })
  }

  async updateWorkflow(id: number, workflow: Partial<Workflow>): Promise<Workflow> {
    return this.request<Workflow>(`/workflows/${id}`, {
      method: 'PUT',
      body: JSON.stringify(workflow),
    })
  }

  async deleteWorkflow(id: number): Promise<{ message: string }> {
    return this.request<{ message: string }>(`/workflows/${id}`, {
      method: 'DELETE',
    })
  }

  async startWorkflow(id: number): Promise<Workflow> {
    return this.request<Workflow>(`/workflows/${id}/start`, {
      method: 'POST',
    })
  }

  async pauseWorkflow(id: number): Promise<Workflow> {
    return this.request<Workflow>(`/workflows/${id}/pause`, {
      method: 'POST',
    })
  }

  async resumeWorkflow(id: number): Promise<Workflow> {
    return this.request<Workflow>(`/workflows/${id}/resume`, {
      method: 'POST',
    })
  }

  async stopWorkflow(id: number): Promise<Workflow> {
    return this.request<Workflow>(`/workflows/${id}/stop`, {
      method: 'POST',
    })
  }

  // Workflow Steps endpoints
  async getWorkflowSteps(workflowId: number): Promise<{ steps: any[] }> {
    return this.request<{ steps: any[] }>(`/workflows/${workflowId}/steps`)
  }

  async createWorkflowStep(workflowId: number, step: any): Promise<any> {
    return this.request<any>(`/workflows/${workflowId}/steps`, {
      method: 'POST',
      body: JSON.stringify(step),
    })
  }

  async updateWorkflowStep(workflowId: number, stepId: string, step: any): Promise<any> {
    return this.request<any>(`/workflows/${workflowId}/steps/${stepId}`, {
      method: 'PUT',
      body: JSON.stringify(step),
    })
  }

  async deleteWorkflowStep(workflowId: number, stepId: string): Promise<{ message: string }> {
    return this.request<{ message: string }>(`/workflows/${workflowId}/steps/${stepId}`, {
      method: 'DELETE',
    })
  }

  async saveWorkflowSteps(workflowId: number, steps: any[]): Promise<{ success: boolean }> {
    // This will batch save all steps by creating/updating/deleting as needed
    return this.request<{ success: boolean }>(`/workflows/${workflowId}/steps/batch`, {
      method: 'PUT',
      body: JSON.stringify({ steps }),
    })
  }

  async updateWorkflowScheduling(workflowId: number, scheduling: {
    enabled: boolean
    cronExpression: string
    timezone: string
  }): Promise<{ success: boolean }> {
    return this.request<{ success: boolean }>(`/workflows/${workflowId}/scheduling`, {
      method: 'PUT',
      body: JSON.stringify({
        cron: scheduling.enabled ? scheduling.cronExpression : null,
        timezone: scheduling.timezone
      }),
    })
  }

  async getWorkflowDetails(workflowId: number): Promise<any> {
    return this.request<any>(`/workflows/${workflowId}`)
  }

  async triggerWorkflowNotifications(workflowId: number, notifySteps: { message: string }[], workflowPayload?: string): Promise<{ success: boolean }> {
    return this.request<{ success: boolean }>(`/workflows/${workflowId}/notifications`, {
      method: 'POST',
      body: JSON.stringify({ notifySteps, workflowPayload }),
    })
  }
}

export const api = new ApiClient()

// Export session types for convenience
export type { BrowserSession, CreateSessionRequest, SessionListResponse }
